import {
  IQBoostPromptConfig,
  IqBoostPromptLookupResult,
  PIIContactRecord,
} from '@maximizer/ai/data-access';
import { Helper } from '@maximizer/ai/ui';
import { OutlookAbEntryDetails } from '@maximizer/outlook/shared/domain';
import { IqPromptSelection } from '../models/iq-boost';

// Tags & prompt names
export const EMAIL_ASSIST_TAG = 'email-assist';
export const EMAIL_DRAFT_TAG = 'email-draft';

// Author-voice guardrail in the backend's Outlook project, fetched by name
// for email-draft prompts.
export const EMAIL_DRAFT_SYSTEM_PROMPT_NAME = 'system/draft-email';

export const EmailAssistPromptName = {
  SummarizeThread: 'iqboost/summarize-thread',
  DraftReply: 'iqboost/draft-reply',
  DraftFollowup: 'iqboost/draft-follow-up',
  ExtractActionItems: 'iqboost/extract-action-items',
} as const;

// Display ordering
const SINGLE_SOURCE_TYPE = 'single';

function findSingleSource(
  prompt: IqPromptSelection,
): { type: string; order?: number } | undefined {
  return prompt.config?.maximizer?.source?.find(
    (sourceItem) => sourceItem.type === SINGLE_SOURCE_TYPE,
  );
}

export function sortPromptsByDisplayOrder(
  prompts: IqPromptSelection[],
): IqPromptSelection[] {
  const singlePrompts = prompts.filter((prompt) => {
    const source = prompt.config?.maximizer?.source;
    if (!source) return true;
    return source.some((sourceItem) => sourceItem.type === SINGLE_SOURCE_TYPE);
  });

  const displayPrompts = singlePrompts.length > 0 ? singlePrompts : prompts;

  return [...displayPrompts].sort((first, second) => {
    const firstOrder = findSingleSource(first)?.order;
    const secondOrder = findSingleSource(second)?.order;
    if (typeof firstOrder !== 'number') {
      return typeof secondOrder !== 'number' ? 0 : 1;
    }
    if (typeof secondOrder !== 'number') return -1;
    return firstOrder - secondOrder;
  });
}

// Draft prompt detection
export function isEmailDraftLookupResult(
  result: IqBoostPromptLookupResult,
): boolean {
  return result.tags?.includes(EMAIL_DRAFT_TAG) ?? false;
}

export function toIqPromptSelection(
  result: IqBoostPromptLookupResult,
  language: string,
): IqPromptSelection {
  return {
    name: result.name,
    title: Helper.getPromptName(
      {
        id: result.name,
        promptMeta: {
          lastConfig: result.config as IQBoostPromptConfig,
          name: result.name,
          versions: [result.version],
          labels: result.labels ?? [],
          tags: result.tags ?? [],
          lastUpdatedAt: '',
        },
      },
      language,
    ),
    isEmailAssist: true,
    isDraft: isEmailDraftLookupResult(result),
    config: result.config,
  };
}

export const INSUFFICIENT_INFO_DRAFT_RESPONSE =
  'Not enough information provided to draft a reply.';

export function isInsufficientInfoDraftResponse(
  text: string | null | undefined,
): boolean {
  if (!text) return false;
  const normalize = (value: string) =>
    value
      .toLowerCase()
      .replace(/[^a-z ]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  return normalize(text).includes(normalize(INSUFFICIENT_INFO_DRAFT_RESPONSE));
}

// Draft message builders
export function buildCurrentUserContextMessage(
  name: string,
  email: string,
): string {
  const trimmedName = name.trim();
  const trimmedEmail = email.trim();
  const identity =
    trimmedName && trimmedEmail
      ? `${trimmedName} (${trimmedEmail})`
      : trimmedName || trimmedEmail;
  return (
    `The current user — the person composing and sending this email — is ${identity}. ` +
    'Write the email in the first person as this person.'
  );
}

export function buildEmailSubjectContextMessage(subject: string): string {
  return `The subject line of this email thread is: "${subject.trim()}"`;
}

// Without this the composer is the only labeled identity in the payload and
// the model greets them instead of the sender.
export function buildReplyRecipientContextMessage(
  name: string,
  email: string,
): string {
  const trimmedName = name.trim();
  const trimmedEmail = email.trim();
  const identity =
    trimmedName && trimmedEmail
      ? `${trimmedName} (${trimmedEmail})`
      : trimmedName || trimmedEmail;
  return (
    `The most recent message in this thread was sent by ${identity} — ` +
    'the person who wrote and signed the quoted email below. ' +
    'Address your reply to this person, never to the current user.'
  );
}

export function buildEmailToReplyToMessage(bodyText: string): string {
  return (
    'The following is the email thread you are replying to. ' +
    'It was written by other participants — do not continue in their voice or restate their message as your own.\n\n' +
    bodyText
  );
}

// Participant conversion
function undefinedIfEmpty(value: string): string | undefined {
  return value.length > 0 ? value : undefined;
}

export function splitDisplayName(displayName: string | undefined): {
  firstName?: string;
  lastName?: string;
} {
  const trimmedName = displayName?.trim();
  if (!trimmedName) return {};

  // Outlook commonly formats display names as "Last, First".
  const commaIndex = trimmedName.indexOf(',');
  if (commaIndex >= 0) {
    const lastName = trimmedName.slice(0, commaIndex).trim();
    const firstName = trimmedName.slice(commaIndex + 1).trim();
    return {
      firstName: undefinedIfEmpty(firstName),
      lastName: undefinedIfEmpty(lastName),
    };
  }

  const nameTokens = trimmedName.split(/\s+/);
  if (nameTokens.length === 1) return { firstName: nameTokens[0] };
  return {
    firstName: nameTokens[0],
    lastName: nameTokens.slice(1).join(' '),
  };
}

export function toParticipantPIIContactRecord(
  participant: Office.EmailAddressDetails,
): PIIContactRecord {
  const fullName = undefinedIfEmpty(participant.displayName?.trim() ?? '');
  return {
    ...splitDisplayName(fullName),
    fullName,
    email: undefinedIfEmpty(participant.emailAddress?.trim() ?? ''),
  };
}

// Entry conversion
function dedupeContactValues(
  values: Array<string | undefined>,
  normalize: (value: string) => string,
): string[] {
  const seenValues = new Set<string>();
  const dedupedValues: string[] = [];
  for (const value of values) {
    const trimmedValue = value?.trim();
    if (!trimmedValue) continue;
    const normalizedValue = normalize(trimmedValue);
    if (!normalizedValue || seenValues.has(normalizedValue)) continue;
    seenValues.add(normalizedValue);
    dedupedValues.push(trimmedValue);
  }
  return dedupedValues;
}

// Guarantees the entry's names get masked by the anonymizer's PII-context
// seeding — its own detection only pattern-matches emails and phones.
export function toPIIContactRecords(
  entry: OutlookAbEntryDetails,
): PIIContactRecord[] {
  const fullName =
    entry.name?.trim() ||
    [entry.firstName, entry.lastName].filter(Boolean).join(' ').trim();
  const addressParts = entry.address
    ? [
        entry.address.address1,
        entry.address.address2,
        entry.address.city,
        entry.address.provinceState,
        entry.address.zipcode,
        entry.address.country,
      ].filter(Boolean)
    : [];

  const emailValues = dedupeContactValues(
    (entry.emails ?? []).map((email) => email.value),
    (value) => value.toLowerCase(),
  );
  const phoneValues = dedupeContactValues(
    (entry.phones ?? []).map((phone) => phone.value),
    (value) => value.replace(/\D/g, ''),
  );

  const baseRecord: PIIContactRecord = {
    firstName: entry.firstName ?? undefined,
    lastName: entry.lastName ?? undefined,
    fullName,
    email: emailValues[0],
    phone: phoneValues[0],
    address: addressParts.length ? addressParts.join(', ') : undefined,
  };

  return [
    baseRecord,
    ...emailValues.slice(1).map((email) => ({ email })),
    ...phoneValues.slice(1).map((phone) => ({ phone })),
  ];
}
